// Has the tax on a lodged return been paid? Ties a return (Tax returns tab, the vault)
// to the money side of /ato-plans: a payment plan, or a statement / notice on Tax paid.
// Pure; the page passes everything in. Testable in Node like atoPlan / atoTax.
//
//   stamp 'paid' — fully paid: the Tax paid item for that amount is covered, or the plan
//                  for that debt is paid off. `date` = the day it became fully paid.
//   stamp 'plan' — the debt sits on a payment plan that's still running (paid / left).
//   stamp 'part' — a matching statement is part-paid.
//   stamp null   — nothing payable, a refund, or no payment recorded (`note` says which).
//
// "Matching" = same entity (names compared without titles / "The trustee for", which is how
// the ATO prints them) AND the same amount to the dollar: a return's amount payable is what
// the plan's debt or the notice's amount is. Other Tax paid items for the same entity
// whose period names the same income year (e.g. a notice of assessment for 2024–25) come
// back as `related`, each with its own status.
import { workPlan, cents } from './atoPlan';
import { workTaxItem } from './atoTax';

const TITLES = /^(MR|MRS|MS|MISS|DR)\s+/;
export function entityKey(name) {
  return String(name || '').toUpperCase()
    .replace(/^THE TRUSTEE FOR\s+/, '').replace(/^THE\s+/, '').replace(TITLES, '')
    .replace(/[^A-Z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
}
// Same entity: identical once tidied, or every word of the shorter name is in the longer
// ("PAUL BOUVENG" ~ "PAUL JAMES BOUVENG"). Needs two words, so "BOUVENG" alone never matches.
export function sameEntity(a, b) {
  const x = entityKey(a);
  const y = entityKey(b);
  if (!x || !y) return false;
  if (x === y) return true;
  const [s, l] = x.split(' ').length <= y.split(' ').length ? [x, y] : [y, x];
  const sw = s.split(' ');
  const lw = new Set(l.split(' '));
  return sw.length >= 2 && sw.every((w) => lw.has(w));
}
const yearDigits = (s) => String(s || '').replace(/\D/g, '');
const within1 = (a, b) => Math.abs(cents(a) - cents(b)) <= 100;

export function returnPayment(ret, { items = [], taxPayments = [], plans = [], planPayments = [] } = {}, today) {
  const result = Number(ret.result);
  const yr = yearDigits(ret.year); // '2024–25' → '202425'
  const liveItems = items.filter((i) => !i.deletedAt && sameEntity(i.entity, ret.entity));

  let out;
  if (!(result > 0)) out = { stamp: null, note: result < 0 ? 'refund' : 'nil' };
  else {
    const plan = plans.find((p) => !p.archived && sameEntity(p.entity, ret.entity) && within1(p.debt, result));
    const item = !plan && liveItems.find((i) => within1(i.amount, result));
    if (plan) {
      const w = workPlan(plan, planPayments, today);
      const pays = planPayments.filter((p) => p.planId === plan.id && !p.deletedAt && p.date).map((p) => p.date).sort();
      out = w.remaining <= 0
        ? { stamp: 'paid', date: pays[pays.length - 1] || '', via: 'plan', plan, paid: w.paid }
        : { stamp: 'plan', via: 'plan', plan, paid: w.paid, left: w.remaining };
    } else if (item) {
      const w = workTaxItem(item, taxPayments, today);
      if (w.status === 'paid' || w.status === 'late') out = { stamp: 'paid', date: w.coveredOn, via: 'item', item, paid: w.paid };
      else if (w.status === 'part') out = { stamp: 'part', via: 'item', item, paid: w.paid, left: w.owing };
      else out = { stamp: null, note: 'unpaid', via: 'item', item, dueDate: item.dueDate, payBy: w.payBy, overdue: w.status === 'overdue' };
    } else out = { stamp: null, note: 'none' };
  }

  // Other statements / notices for the same income year (e.g. a notice of assessment).
  const primaryId = out.item && out.item.id;
  out.related = yr.length >= 6
    ? liveItems.filter((i) => i.id !== primaryId && yearDigits(i.period).includes(yr))
      .map((i) => ({ item: i, work: workTaxItem(i, taxPayments, today) }))
    : [];
  return out;
}
